import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { format, subDays } from 'date-fns';
import { AlertTriangle, Filter, LineChart, BarChart3 } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  BarChart,
  Bar,
  LabelList
} from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { fetchComplaintsNegativity } from '../api';
import { GRAPH_REFRESH_INTERVAL_MS } from '../config/constants';
import useTimeZone from '../hooks/useTimeZone';
import { formatDayMonthYear } from '../utils/dateTime';

const defaultRange = {
  from: subDays(new Date(), 29),
  to: new Date()
};

const COMPLAINT_COLOR = '#f97316';
const LABEL_COLOR = 'hsl(var(--foreground))';

const numberFormatter = (value) =>
  typeof value === 'number' ? value.toLocaleString('ru-RU') : value ?? '—';

const formatPercent = (value, total) => {
  if (!total || total <= 0 || typeof value !== 'number') return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

const tooltipFormatter = (entry) => {
  if (!entry || typeof entry.value !== 'number') {
    return '—';
  }
  const total = entry.payload?.total ?? 0;
  return `${entry.value.toLocaleString('ru-RU')} (${formatPercent(entry.value, total)})`;
};

const createPercentLabelRenderer = (data) => (props) => {
  const { x, y, width, value, index } = props;
  if (value === null || value === undefined) return null;
  const total = data?.[index]?.total ?? 0;
  const baseX = typeof x === 'number' ? x : 0;
  const centerX = typeof width === 'number' ? baseX + width / 2 : baseX;
  return (
    <text
      x={centerX}
      y={(y ?? 0) - 6}
      textAnchor="middle"
      fill={LABEL_COLOR}
      fontSize="9"
      fontWeight="600"
    >
      {`${numberFormatter(value)} (${formatPercent(value, total)})`}
    </text>
  );
};

const createLinePercentLabelRenderer = (data) => (props) => {
  const { x, y, value, index } = props;
  if (value === null || value === undefined) return null;
  const total = data?.[index]?.total ?? 0;
  return (
    <text
      x={typeof x === 'number' ? x : 0}
      y={(y ?? 0) - 8}
      textAnchor="middle"
      fill={LABEL_COLOR}
      fontSize="9"
      fontWeight="600"
    >
      {`${numberFormatter(value)} (${formatPercent(value, total)})`}
    </text>
  );
};

const formatPointLabel = (point, timeZone) => {
  if (!point) return '—';
  const granularity = point.granularity?.toLowerCase();
  if (granularity === 'hour' || granularity === 'minute') {
    return point.label ?? '—';
  }
  const raw = point.day ?? point.label;
  if (!raw) return '—';
  if (raw instanceof Date || typeof raw === 'string') {
    const formatted = formatDayMonthYear(raw, timeZone);
    return formatted === '—' ? String(raw) : formatted;
  }
  return String(raw);
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border px-3 py-2 text-sm shadow-md"
      style={{
        backgroundColor: 'hsl(var(--card))',
        color: 'hsl(var(--card-foreground))',
        borderColor: 'hsl(var(--border))'
      }}
    >
      <div className="mb-1 font-medium">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: entry.color }} />
          <span>
            {entry.name ?? entry.dataKey}: {tooltipFormatter(entry)}
          </span>
        </div>
      ))}
    </div>
  );
};

const ButtonReload = ({ loading, onReload }) => (
  <button
    type="button"
    onClick={onReload}
    disabled={loading}
    className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
  >
    <Filter className="mr-2 h-4 w-4" />
    {loading ? 'Обновление...' : 'Обновить'}
  </button>
);

const ChartToggleButton = ({ chartType, onToggle }) => {
  const isBar = chartType === 'bar';
  const Icon = isBar ? LineChart : BarChart3;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="inline-flex items-center rounded-md border px-3 py-1 text-sm text-muted-foreground hover:bg-muted"
    >
      <Icon className="mr-2 h-4 w-4" />
      {isBar ? 'Линия' : 'Столбцы'}
    </button>
  );
};

const ComplaintsNegativityChart = ({ dateRange, callType = 'all' }) => {
  const [chartType, setChartType] = useState('line');
  const [points, setPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isFetching = useRef(false);
  const { timeZone } = useTimeZone();

  const activeDateRange = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return dateRange;
    }
    return defaultRange;
  }, [dateRange]);

  const dateRangeLabel = useMemo(() => {
    if (!activeDateRange?.from || !activeDateRange?.to) {
      return 'за выбранный период';
    }
    try {
      const fromLabel = formatDayMonthYear(activeDateRange.from, timeZone);
      const toLabel = formatDayMonthYear(activeDateRange.to, timeZone);
      return `за период ${fromLabel} — ${toLabel}`;
    } catch {
      return 'за выбранный период';
    }
  }, [activeDateRange, timeZone]);

  const load = useCallback(async (showSpinner = true) => {
    if (!activeDateRange?.from || !activeDateRange?.to || isFetching.current) {
      return;
    }

    if (showSpinner) {
      setIsLoading(true);
    }

    isFetching.current = true;
    setError(null);
    try {
      const params = {
        date_from: format(activeDateRange.from, 'yyyy-MM-dd'),
        date_to: format(activeDateRange.to, 'yyyy-MM-dd'),
        call_type: callType === 'all' ? undefined : callType
      };
      const data = await fetchComplaintsNegativity(params);
      setPoints(Array.isArray(data) ? data : []);
    } catch {
      setError('Не удалось загрузить данные по жалобам.');
    } finally {
      isFetching.current = false;
      if (showSpinner) {
        setIsLoading(false);
      }
    }
  }, [activeDateRange, callType]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      load(false);
    }, GRAPH_REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [load]);

  const chartData = useMemo(
    () =>
      (points ?? []).map((point) => ({
        label: formatPointLabel(point, timeZone),
        complaints: point.complaints ?? 0,
        total: point.totalCalls ?? 0
      })),
    [points, timeZone]
  );

  const maxValue = useMemo(() => {
    if (!chartData.length) return 1;
    return Math.max(1, ...chartData.map((p) => p.complaints));
  }, [chartData]);

  const toggleChartType = () => {
    setChartType((prev) => (prev === 'bar' ? 'line' : 'bar'));
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Жалобы
          </CardTitle>
          <CardDescription>Распределение {dateRangeLabel}</CardDescription>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3">
          <ChartToggleButton chartType={chartType} onToggle={toggleChartType} />
          <ButtonReload loading={isLoading} onReload={load} />
        </div>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {chartData.length === 0 && !isLoading && (
          <p className="text-sm text-muted-foreground">Нет данных для отображения.</p>
        )}
        {chartData.length > 0 && (
          <div className="relative h-64 w-full min-w-0 rounded-md bg-muted/30 px-2 py-4">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={160}>
              {chartType === 'bar' ? (
                <BarChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis
                    width={60}
                    domain={[0, Math.ceil(1.1 * maxValue)]}
                    tickFormatter={numberFormatter}
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip wrapperStyle={{ zIndex: 30 }} content={<ChartTooltip />} />
                  <Bar
                    dataKey="complaints"
                    name="Жалобы"
                    fill={COMPLAINT_COLOR}
                    radius={[4, 4, 0, 0]}
                  >
                    <LabelList
                      dataKey="complaints"
                      content={createPercentLabelRenderer(chartData)}
                    />
                  </Bar>
                </BarChart>
              ) : (
                <ReLineChart data={chartData} margin={{ top: 12, right: 24, left: 0, bottom: 32 }}>
                  <CartesianGrid strokeDasharray="4 4" stroke="rgba(148,163,184,0.25)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis
                    width={60}
                    domain={[0, Math.ceil(1.1 * maxValue)]}
                    tickFormatter={numberFormatter}
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip wrapperStyle={{ zIndex: 30 }} content={<ChartTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="complaints"
                    name="Жалобы"
                    stroke={COMPLAINT_COLOR}
                    strokeWidth={2}
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                  >
                    <LabelList
                      dataKey="complaints"
                      content={createLinePercentLabelRenderer(chartData)}
                    />
                  </Line>
                </ReLineChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ComplaintsNegativityChart;
